import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { StateMessageComponent } from '@groundworks/ngx-groundworks-ui';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AttachmentGroupInfo } from '../../../sdk/sdk';
import { AttachmentService } from '../../../services/attachment.service';

export interface AttachmentEditResult {
  attachmentId: string;
  groupId: string | null;
  groupName: string | null;
  description: string;
}

@Component({
  selector: 'app-attachment-edit-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, StateMessageComponent],
  templateUrl: './attachment-edit-modal.component.html',
})
export class AttachmentEditModalComponent implements OnInit {
  private readonly attachmentService = inject(AttachmentService);
  readonly activeModal = inject(NgbActiveModal);

  attachmentId = '';
  initialGroupId: string | null = null;
  initialDescription = '';

  readonly updating = signal(false);

  readonly attachmentGroups = signal<AttachmentGroupInfo[]>([]);
  readonly loadingGroups = signal(false);
  readonly loadGroupsError = signal<string | null>(null);

  readonly editForm = new FormGroup({
    groupId: new FormControl<string | null>(null),
    description: new FormControl<string>(''),
  });

  constructor() {
    this.fetchAttachmentGroups();
  }

  ngOnInit(): void {
    // Set initial values
    this.editForm.patchValue({
      groupId: this.initialGroupId,
      description: this.initialDescription,
    });
  }

  fetchAttachmentGroups(): void {
    this.loadingGroups.set(true);
    this.loadGroupsError.set(null);
    this.attachmentService.getAttachmentGroups().subscribe({
      next: (groups) => {
        this.attachmentGroups.set(groups ?? []);
        this.loadingGroups.set(false);
      },
      error: () => {
        this.loadGroupsError.set('Unable to load attachment groups.');
        this.loadingGroups.set(false);
      },
    });
  }

  onCancel(): void {
    this.activeModal.dismiss('cancel');
  }

  onSave(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.updating.set(true);

    const groupId = this.editForm.value.groupId ?? null;
    const description = this.editForm.value.description ?? '';

    const groupName = groupId
      ? this.attachmentGroups().find((g) => g.id === groupId)?.name ?? null
      : null;

    this.attachmentService.updateAttachment(this.attachmentId, groupId, description).subscribe({
      next: () => {
        this.updating.set(false);
        this.activeModal.close({ attachmentId: this.attachmentId, groupId, groupName, description } as AttachmentEditResult);
      },
      error: (error) => {
        this.updating.set(false);
        console.error('Update error:', error);
      },
    });
  }
}

